import { RETR_CCOMP } from "@techstark/opencv-js";
import Utils from "@utils/utils"
import axios from 'axios'

/**
 * 根据交付图命名规则格式化命名
 * 默认"$S-#"
 * 规则说明：
 * "$S" 代表货号
 * "#" 代表数字
 * 例1："$S-#" 按 货号-1、货号-2、货号-3 格式命名
 * 例2："#-AI" 按 1-AI、2-AI、3-AI 格式命名
 * 例3："AI-##" 按 AI-01、AI-02、AI-03 格式命名
 * ...
 * @param {*} param0 { skuCode, index }
 * @param {*} rule 
 * @returns 格式化后的文件名
 */
const formatFilenameByRule = ({
    skuCode, index
}, rule="$S-#") => {
    // 过滤掉不能作为文件名的字符 /\:*?"<>|
    const sanitizedRule = rule.replace(/[\/\\:*?"<>|]/g, '');
    // 替换货号占位符 $S
    let result = sanitizedRule.replace(/\$S/g, skuCode);
    // 匹配连续的 # 占位符，根据 # 的数量决定数字的补零位数
    result = result.replace(/#+/g, (match) => {
        const len = match.length;
        return String(index).padStart(len, '0');
    });
    return result;
}

const normalizeSkuList = (skuList, images) => {
    const skuIds = images.map(item => item.skuId)
    const filteredSkuList = skuList.filter(item => skuIds.includes(item.id))
    const formatSkuListMap = {}
    for (let sku of filteredSkuList) {
        const {
            skuCode,
            deliveryImages,
            skuImages,
            skuImageMap,
            matchImages,
            matchImageMap,
            refImages,
            refImageMap,
        } = sku
        if (!formatSkuListMap[skuCode]) {
            formatSkuListMap[skuCode] = { ...sku }
        } else {
            formatSkuListMap[skuCode] = {
                ...formatSkuListMap[skuCode],
                deliveryImages: [...(formatSkuListMap[skuCode].deliveryImages || []), ...(deliveryImages || [])],
                skuImages: [...(formatSkuListMap[skuCode].skuImages || []), ...(skuImages || [])],
                skuImageMap: [...(formatSkuListMap[skuCode].skuImageMap || []), ...(skuImageMap || [])],
                matchImages: [...(formatSkuListMap[skuCode].matchImages || []), ...(matchImages || [])],
                matchImageMap: [...(formatSkuListMap[skuCode].matchImageMap || []), ...(matchImageMap || [])],
                refImages: [...(formatSkuListMap[skuCode].refImages || []), ...(refImages || [])],
                refImageMap: [...(formatSkuListMap[skuCode].refImageMap || []), ...(refImageMap || [])],
            }
        }
    }
    return Object.values(formatSkuListMap)
}

const countDownloadFiles = (skuList, images, types) => {
    let total = 0
    const imageIdSet = new Set(images.map(item => item.imageId))
    for (let sku of skuList) {
        const deliveryImages = sku.deliveryImages || []
        const hasThisSku = deliveryImages.some(item => imageIdSet.has(item.imageId))
        if (!hasThisSku) continue

        if (types.includes('origin') && (!sku.deliveryTypes || sku.deliveryTypes.includes('origin'))) {
            let skuImages = sku.skuImages?.filter(item => !!item) || []
            if (skuImages.length == 0) {
                skuImages = deliveryImages.filter(item => item.skuImage).map(item => item.skuImage)
            }
            total += [...new Set(skuImages)].length
        }

        if (types.includes('match') && (!sku.deliveryTypes || sku.deliveryTypes.includes('match'))) {
            let matchImages = sku.matchImages?.filter(item => !!item) || []
            if (matchImages.length == 0) {
                matchImages = deliveryImages
                    .filter(item => item.matchImages?.length > 0 || item.matchClothImage)
                    .flatMap(item => {
                        if (item.matchImages?.length > 0) return item.matchImages.map(matchItem => matchItem.url)
                        return [item.matchClothImage]
                    })
            }
            total += [...new Set(matchImages)].length
        }

        if (types.includes('reference') && (!sku.deliveryTypes || sku.deliveryTypes.includes('reference'))) {
            let refImages = sku.refImages?.filter(item => !!item) || []
            if (refImages.length == 0) {
                refImages = deliveryImages
                    .filter(item => item.refImages?.length > 0 || item.refImage)
                    .flatMap(item => {
                        if (item.refImages?.length > 0) return item.refImages.map(refItem => refItem.url)
                        return [item.refImage]
                    })
            }
            total += [...new Set(refImages)].length
        }

        if (types.includes('delivery') && (!sku.deliveryTypes || sku.deliveryTypes.includes('delivery'))) {
            total += deliveryImages.filter(item => imageIdSet.has(item.imageId)).length
        }
    }
    return total
}

const resolveFolderState = ({ packageType, level1Folder, skuFolderName, currentFolderType }) => {
    let currentFolder = level1Folder
    let level2Folder = null
    let level3Folder = null
    if (packageType == 0) { // 按商品分文件下载：批次/skuCode/素材+交付图
        level2Folder = level1Folder.folder(skuFolderName)
        currentFolder = level2Folder
    } else if (packageType == 2) { // 素材图单独文件夹: 批次/skuCode/素材、交付图
        level2Folder = level1Folder.folder(skuFolderName)
        if (currentFolderType === 'delivery') {
            currentFolder = level2Folder
        } else {
            level3Folder = level2Folder.folder('素材')
            currentFolder = level3Folder
        }
    }
    return {
        currentFolder,
        level2Folder,
        level3Folder,
    }
}

// 下载阶段做限并发，避免一次性拉太多文件把客户浏览器或网络压垮。
const DOWNLOAD_CONCURRENCY = 2

const createDownloadFileError = ({ file, message, cause }) => {
    const detail = [
        message,
        file?.filename ? `filename: ${file.filename}` : null,
        file?.url ? `url: ${file.url}` : null,
    ].filter(Boolean).join(' | ')
    const error = new Error(detail)
    if (cause) error.cause = cause
    return error
}

const validateFileData = (file) => {
    const byteLength = file?.data?.byteLength
    if (typeof byteLength !== 'number') {
        throw createDownloadFileError({
            file,
            message: '下载文件数据为空或不可用',
        })
    }
    return byteLength
}

const fetchFileBatch = async (files, startIndex, concurrency = DOWNLOAD_CONCURRENCY) => {
    const batch = files.slice(startIndex, startIndex + concurrency)
    return Promise.all(batch.map(async (file) => {
        try {
            const response = await axios.get(file.url, { responseType: 'arraybuffer' })
            const result = {
                ...file,
                data: response.data,
            }
            validateFileData(result)
            return result
        } catch (err) {
            if (err instanceof Error && err.message?.includes('filename:')) {
                throw err
            }
            throw createDownloadFileError({
                file,
                message: '下载文件失败',
                cause: err,
            })
        }
    }))
}

const getFolderKey = ({ packageType, skuFolderName, currentFolderType }) => {
    if (packageType == 0) return `sku:${skuFolderName}`
    if (packageType == 2) return `sku:${skuFolderName}:${currentFolderType}`
    return 'root'
}

const appendFileToZip = ({ currentFolder, filenameCountMap, folderKey, filename, data }) => {
    let actualFilename = filename
    const countKey = `${folderKey}|${filename}`
    const count = filenameCountMap.get(countKey) || 0
    if (count > 0) {
        const extensionIndex = filename.lastIndexOf('.')
        if (extensionIndex === -1) {
            actualFilename = `${filename}(${count})`
        } else {
            const name = filename.slice(0, extensionIndex)
            const extension = filename.slice(extensionIndex + 1)
            actualFilename = `${name}(${count}).${extension}`
        }
    }
    currentFolder.file(actualFilename, data, { binary: true })
    filenameCountMap.set(countKey, count + 1)
    return actualFilename
}

const writeDownloadedFile = async ({
    file,
    currentFolder,
    filenameCountMap,
    completedFiles,
    totalFiles,
    progressCallback,
    overflowCallback,
    packageType,
    level1Folder,
    skuFolderName,
    currentFolderType,
}) => {
    const folderKey = getFolderKey({
        packageType,
        skuFolderName,
        currentFolderType,
    })
    const byteLength = validateFileData(file)
    // 写入前先判断当前分包是否还能容纳这个文件，避免先写进去再触发大包打包。
    const preWriteZip = await overflowCallback?.({
        stage: 'beforeWrite',
        byteLength,
    })
    let nextLevel1Folder = level1Folder
    let nextCurrentFolder = currentFolder
    if (preWriteZip) {
        nextLevel1Folder = preWriteZip
        ;({ currentFolder: nextCurrentFolder } = resolveFolderState({
            packageType,
            level1Folder: nextLevel1Folder,
            skuFolderName,
            currentFolderType,
        }))
    }
    appendFileToZip({
        currentFolder: nextCurrentFolder,
        filenameCountMap,
        folderKey,
        filename: file.filename,
        data: file.data,
    })
    const nextCompletedFiles = completedFiles + 1
    progressCallback?.({
        stage: 'download',
        completed: nextCompletedFiles,
        total: totalFiles,
        skuCode: file.skuCode,
        filename: file.filename,
    })
    // 写入后再做一次兜底检查，覆盖单文件较大或边界值场景。
    const newZip = await overflowCallback?.({
        stage: 'afterWrite',
        byteLength,
    })
    if (newZip) {
        nextLevel1Folder = newZip
        ;({ currentFolder: nextCurrentFolder } = resolveFolderState({
            packageType,
            level1Folder: nextLevel1Folder,
            skuFolderName,
            currentFolderType,
        }))
    }
    return {
        completedFiles: nextCompletedFiles,
        level1Folder: nextLevel1Folder,
        currentFolder: nextCurrentFolder,
    }
}

const downloadAsZip = async (skuList, images, types, packageType, level1Folder, deliveryNamingRule, overflowCallback, progressCallback) => {
    try {
        const filenameCountMap = new Map()
        let deliveryImageIndex = 0
        const formatSkuList = normalizeSkuList(skuList, images)
        const totalFiles = countDownloadFiles(formatSkuList, images, types)
        let completedFiles = 0
        progressCallback?.({
            stage: 'download',
            completed: completedFiles,
            total: totalFiles,
        })
        for (let sku of formatSkuList) {
            // 交付内容
            const deliveryImages = sku.deliveryImages
            const hasThisSku = deliveryImages.find(item => images.find(item2 => item2.imageId == item.imageId))
            if (!hasThisSku) continue
            // 商品编号
            // 交付批次下载时限制将批次名中的特殊字符替换成下划线
            const skuFolderName = Utils.sanitizeString(sku.skuCode || sku.skuNumber) // 没填商品货号，使用skuNumber作为skuid
            // 创建文件夹
            let currentFolder = null
            let currentFolderType = packageType == 2 ? 'material' : 'default'
            ;({ currentFolder } = resolveFolderState({
                packageType,
                level1Folder,
                skuFolderName,
                currentFolderType,
            }))
            if (packageType == 0 || packageType == 2) {
                deliveryImageIndex = 0 // 重置交付图索引
            }

            // 源素材
            if (types.includes('origin') && (!sku.deliveryTypes || sku.deliveryTypes.includes('origin'))) {
                let skuImages = sku.skuImages?.filter(item => !!item) || []
                let skuImageMap = sku.skuImageMap || []
                if (skuImages.length == 0) { // 20260331 修复skuImages为空时，从deliveryImages中获取skuImage
                    skuImages = sku.deliveryImages.filter(item => item.skuImage).map(item => item.skuImage)
                    skuImageMap = [...sku.deliveryImages.filter(item => item.skuImage).map(item => ({url: item.skuImage, name: item.skuImageName})), ...skuImageMap].filter(item => item.name)
                }
                skuImages = [...new Set(skuImages)]
                const originFiles = skuImages.map((url) => {
                    const urlName = url.split('?')[0].split('/').pop().split('.').shift()
                    const name = skuImageMap.find(item => item.url == url)?.name || urlName
                    const fileExtension = url.split('?')[0].split('.').pop()
                    return {
                        url,
                        filename: `${name}.${fileExtension}`,
                        skuCode: skuFolderName,
                    }
                })
                for (let startIndex = 0; startIndex < originFiles.length; startIndex += DOWNLOAD_CONCURRENCY) {
                    const batchResults = await fetchFileBatch(originFiles, startIndex)
                    for (let file of batchResults) {
                        const nextState = await writeDownloadedFile({
                            file,
                            currentFolder,
                            filenameCountMap,
                            completedFiles,
                            totalFiles,
                            progressCallback,
                            overflowCallback,
                            packageType,
                            level1Folder,
                            skuFolderName,
                            currentFolderType,
                        })
                        completedFiles = nextState.completedFiles
                        level1Folder = nextState.level1Folder
                        currentFolder = nextState.currentFolder
                    }
                }
            }
            // 搭配图
            if (types.includes('match') && (!sku.deliveryTypes || sku.deliveryTypes.includes('match'))) {
                let matchImages = sku.matchImages?.filter(item => !!item) || []
                let matchImageMap = sku.matchImageMap || []
                if (matchImages.length == 0) { // 20260331 修复matchImages为空时，从deliveryImages中获取matchClothImage
                    matchImages = sku.deliveryImages
                                    .filter(item => item.matchImages?.length > 0 || item.matchClothImage)
                                    .flatMap(item => {
                                        if (item.matchImages?.length > 0) return item.matchImages.map(item => item.url)
                                        else return [item.matchClothImage]
                                    })
                    matchImageMap = [
                        ...sku.deliveryImages
                            .filter(item => item.matchImages?.length > 0 || item.matchClothImage)
                            .flatMap(item => {
                                if (item.matchImages?.length > 0) return item.matchImages.map(item => ({url: item.url, name: item.name}))
                                else return [{url: item.matchClothImage, name: item.matchClothName}]
                            }),
                        ...matchImageMap
                    ].filter(item => item.name)
                }
                matchImages = [...new Set(matchImages)]
                const matchFiles = matchImages.map((url) => {
                    const urlName = url.split('?')[0].split('/').pop().split('.').shift()
                    const name = matchImageMap.find(item => item.url == url)?.name || urlName
                    const fileExtension = url.split('?')[0].split('.').pop()
                    return {
                        url,
                        filename: `${name}.${fileExtension}`,
                        skuCode: skuFolderName,
                    }
                })
                for (let startIndex = 0; startIndex < matchFiles.length; startIndex += DOWNLOAD_CONCURRENCY) {
                    const batchResults = await fetchFileBatch(matchFiles, startIndex)
                    for (let file of batchResults) {
                        const nextState = await writeDownloadedFile({
                            file,
                            currentFolder,
                            filenameCountMap,
                            completedFiles,
                            totalFiles,
                            progressCallback,
                            overflowCallback,
                            packageType,
                            level1Folder,
                            skuFolderName,
                            currentFolderType,
                        })
                        completedFiles = nextState.completedFiles
                        level1Folder = nextState.level1Folder
                        currentFolder = nextState.currentFolder
                    }
                }
            }
            // 参考图
            if (types.includes('reference') && (!sku.deliveryTypes || sku.deliveryTypes.includes('reference'))) {
                let refImages = sku.refImages?.filter(item => !!item) || []
                let refImageMap = sku.refImageMap || []
                if (refImages.length == 0) { // 20260331 修复refImages为空时，从deliveryImages中获取refImage
                    refImages = sku.deliveryImages
                                    .filter(item => item.refImages?.length > 0 || item.refImage)
                                    .flatMap(item => {
                                        if (item.refImages?.length > 0) return item.refImages.map(item => item.url)
                                        else return [item.refImage]
                                    })
                    refImageMap = [
                        ...sku.deliveryImages
                            .filter(item => item.refImages?.length > 0 || item.refImage)
                            .flatMap(item => {
                                if (item.refImages?.length > 0) return item.refImages.map(item => ({url: item.url, name: item.name}))
                                return []
                            }),
                        ...refImageMap
                    ].filter(item => item.name)
                }
                refImages = [...new Set(refImages)]
                const referenceFiles = refImages.map((url) => {
                    const urlName = url.split('?')[0].split('/').pop().split('.').shift()
                    const name = refImageMap.find(item => item.url == url)?.name || urlName
                    const fileExtension = url.split('?')[0].split('.').pop()
                    return {
                        url,
                        filename: `${name}.${fileExtension}`,
                        skuCode: skuFolderName,
                    }
                })
                for (let startIndex = 0; startIndex < referenceFiles.length; startIndex += DOWNLOAD_CONCURRENCY) {
                    const batchResults = await fetchFileBatch(referenceFiles, startIndex)
                    for (let file of batchResults) {
                        const nextState = await writeDownloadedFile({
                            file,
                            currentFolder,
                            filenameCountMap,
                            completedFiles,
                            totalFiles,
                            progressCallback,
                            overflowCallback,
                            packageType,
                            level1Folder,
                            skuFolderName,
                            currentFolderType,
                        })
                        completedFiles = nextState.completedFiles
                        level1Folder = nextState.level1Folder
                        currentFolder = nextState.currentFolder
                    }
                }
            }
            // 交付图
            if (types.includes('delivery') && (!sku.deliveryTypes || sku.deliveryTypes.includes('delivery'))) {
                if (packageType == 2) {
                    currentFolderType = 'delivery'
                    ;({ currentFolder } = resolveFolderState({
                        packageType,
                        level1Folder,
                        skuFolderName,
                        currentFolderType,
                    }))
                }
                const deliveryFiles = []
                for (let i = 0; i < deliveryImages.length; i++) {
                    const delivery = deliveryImages[i]
                    const hasThis = images.find(item => item.imageId == delivery.imageId)
                    if (!hasThis) continue
                    if (hasThis.deliveryImage) {
                        const url = hasThis.deliveryImage
                        // 交付批次下载时限制将批次名中的特殊字符替换成下划线
                        const skuCode = Utils.sanitizeString(hasThis.skuCode)
                        const fileExtension = url.split('?')[0].split('.').pop()
                        const filename = formatFilenameByRule({ skuCode, index: deliveryImageIndex+1 }, deliveryNamingRule)
                        deliveryFiles.push({
                            url,
                            filename: `${filename}.${fileExtension}`,
                            skuCode,
                        })
                    }
                    deliveryImageIndex++
                }
                for (let startIndex = 0; startIndex < deliveryFiles.length; startIndex += DOWNLOAD_CONCURRENCY) {
                    const batchResults = await fetchFileBatch(deliveryFiles, startIndex)
                    for (let file of batchResults) {
                        const nextState = await writeDownloadedFile({
                            file,
                            currentFolder,
                            filenameCountMap,
                            completedFiles,
                            totalFiles,
                            progressCallback,
                            overflowCallback,
                            packageType,
                            level1Folder,
                            skuFolderName,
                            currentFolderType,
                        })
                        completedFiles = nextState.completedFiles
                        level1Folder = nextState.level1Folder
                        currentFolder = nextState.currentFolder
                    }
                }
            }
        }
    } catch (err) {
        console.error(err)
        throw err
    }
}

export { downloadAsZip, formatFilenameByRule }

